import { computed, ref, watch } from 'vue'
import { isOffline, getExternalAuthsError, rememberUserCredentials } from '@web/common/Utils'
import Analytics from '@web/services/Analytics/Analytics'
import * as AnalyticEvents from '@web/services/Analytics/events'
import OneSignal from '@web/common/OneSignal'
import sentry, { sentryConfigure } from '@web/common/sentry'
import { useStore } from '@web/store'
import Token from '@web/common/token'
import { redirectToReturnUrl } from '@web/common/returnUrl'
import { consumePendingUrlToken } from '@web/common/stripUrlToken'
import LocalStorage from '@web/common/LocalStorage'
import { IS_WEB } from '@web/common/config'
import Swal from 'sweetalert2'
import i18n from '@web/plugins/i18n'
import PAGE_NAME from '@web/consts/PAGE_NAME'

export default function ({ router, serverErrors, displayServerErrors, isLoadDataAfterSuccessLogin }) {
  const store = useStore()

  const email = ref('')
  const password = ref('')
  const save = ref(true)
  const showPassword = ref(false)
  const logo = computed(() => store.getters['assets/logo'])
  const clients = computed(() => store.getters['assets/authCollection'])
  const username = computed({
    get () {
      return email.value
    },
    set (value: string) {
      email.value = value.toLowerCase()
    }
  })
  const notice = computed({
    get () {
      return store.getters['user/notice']
    },
    set (value: string) {
      store.commit('user/setNotice', value)
    }
  })
  const showRestorePasswordButton = ref(false)

  const isLoginBtnDisabled = computed(() => !email.value.length || !password.value.length)

  async function redirectAfterLogin () {
    const store = useStore()

    if (isLoadDataAfterSuccessLogin) {
      await store.dispatch('fetchAllData')
    }

    const returnUrlFromQuery: string | null = router.currentRoute.value.query.returnUrl
    const returnUrlFromStore: string = store.state.main.returnUrl

    function openInternal (route): void {
      router.push(route)
    }

    // Внешний returnUrl — только на домен платформы: к нему дописывается JWT.
    // Чужой адрес отклоняется, и пользователь остаётся в ЛК.
    if (redirectToReturnUrl(returnUrlFromQuery)) {
      return
    }
    if (/^https?:\/\//.test(returnUrlFromStore)) {
      if (!redirectToReturnUrl(returnUrlFromStore)) {
        openInternal({ name: PAGE_NAME.HOME })
      }
    } else if (returnUrlFromStore && returnUrlFromStore !== '/') {
      openInternal(returnUrlFromStore)
    } else {
      openInternal({ name: PAGE_NAME.HOME })
    }
  }

  email.value = email.value || store.getters['user/email']
  watch(email, (value) => {
    store.commit('user/setField', { key: 'email', value })
  })

  function afterLogin (user) {
    redirectAfterLogin()
    if (IS_WEB) {
      OneSignal.setExternalUserId(user.id)
    }
  }

  function catchError (e) {
    if (e.errors) {
      serverErrors.value = e.errors
      if (!Object.keys(e.errors).includes('password')) {
        showRestorePasswordButton.value = false
      } else {
        Analytics.send({
          category: 'restore_password',
          action: 'error_from_server',
          label: displayServerErrors(e.errors)[0]
        })
        showRestorePasswordButton.value = true
      }
    } else {
      notice.value = i18n.global.t('error.signIn')
      sentry.captureException(e)
    }
  }

  const submit = async (errors?: { [key: string]: string }) => {
    if (isLoginBtnDisabled.value || (errors && Object.keys(errors).length)) {
      return
    }

    try {
      if (!save.value) return

      save.value = false
      serverErrors.value = []
      const user = await store.dispatch('user/login', { email: email.value, password: password.value })
      AnalyticEvents.login({
        method: 'email',
        status: 'success',
        userId: user.id
      })
      store.dispatch('community/fetchCachedCommunities')
      sentryConfigure(user, store.getters['user/username'])

      afterLogin(user)
    } catch (e) {
      AnalyticEvents.login({
        method: 'email',
        status: 'fail'
      })
      catchError(e)
    } finally {
      save.value = true
    }
  }

  function autologin (): boolean {
    const emailLocal = LocalStorage.get('u_email')
    const passwordLocal = LocalStorage.get('u_password')

    if (!!emailLocal && !!passwordLocal) {
      email.value = emailLocal
      password.value = passwordLocal

      if (isOffline()) {
        return false
      }
      submit()
      return true
    }

    return false
  }

  function rememberUser () {
    if (email.value && password.value) {
      rememberUserCredentials(email.value, password.value)
    }
  }

  watch(router.currentRoute, (newRoute) => {
    const query: Dictionary<string> = newRoute.query as Dictionary<string>
    const socNetworks = {
      apple: 'Apple',
      google: 'Google',
      facebook: 'Facebook',
      vkontakte: 'Вконтакте'
    }
    if (query.action === 'login') {
      if (query.status === 'fail') {
        let msg = getExternalAuthsError({ errorCode: query.errorCode, errorMessage: query.errorMessage })
        if (query.errorCode === 'login_no_link') {
          msg = i18n.global.t('loginSocialNetworks', { socNetworks: socNetworks[query.method] || query.method })
        }
        Swal.fire({
          title: i18n.global.t('Error'),
          text: msg,
          confirmButtonText: i18n.global.t('good'),
          icon: 'error'
        })

        router.replace({ query: {} })
      }
      if (query.status === 'success') {
        // Обычно токен уже снят с адреса при загрузке (stripUrlToken) — берём его
        // оттуда; query.token остаётся запасным источником для переходов внутри SPA.
        const token = query.token ? String(query.token) : consumePendingUrlToken()
        if (!token) {
          Swal.fire({
            title: i18n.global.t('Error'),
            text: getExternalAuthsError({ errorCode: '', errorMessage: '' }),
            confirmButtonText: i18n.global.t('good'),
            icon: 'error'
          })
          router.replace({ query: {} })
          return
        }
        Token.set(token)
        // Чистим query с токеном сразу после сохранения: user/fetch и событие
        // login идут следом, а gtag подставляет в них текущий location.href —
        // иначе JWT уедет в аналитику вместе с page_location.
        router.replace({ query: {} })
        store.dispatch('user/fetch')
          .then(user => {
            AnalyticEvents.login({
              userId: user.id,
              method: query.method,
              status: 'success'
            })

            afterLogin(user)
          })
      }
    }
  }, {
    immediate: true
  })

  return {
    email,
    username,
    password,
    isLoginBtnDisabled,
    save,
    showPassword,
    showRestorePasswordButton,
    logo,
    clients,
    notice,
    submit,
    serverErrors,
    autologin,
    rememberUser
  }
}
